import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, HelpCircle, Mail, ExternalLink, Bug, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

// WhatsApp icon SVG (Lucide doesn't have it)
const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

export function HelpButton() {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const whatsappNumber = "5547991001203";
    const supportEmail = "brufab222@gmail.com";

    const handleWhatsApp = () => {
        const message = encodeURIComponent("Olá! Preciso de ajuda com a Publee.");
        window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    };

    const handleEmail = () => {
        navigator.clipboard.writeText(supportEmail).then(() => {
            toast({
                title: "Email copiado! 📋",
                description: `${supportEmail} copiado para a área de transferência.`,
            });
        }).catch(() => {
            window.open(`mailto:${supportEmail}?subject=Suporte Publee`, '_blank');
        });
    };

    const handleBugReport = () => {
        const bugMessage = encodeURIComponent("Olá! Encontrei um bug na Publee:\n\n[Descreva o bug aqui]");
        window.open(`https://wa.me/${whatsappNumber}?text=${bugMessage}`, '_blank');
    };

    const handleFAQ = () => {
        navigate('/suporte');
        setIsOpen(false);
    };

    return (
        <div className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute bottom-16 right-0 w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
                    >
                        <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border">
                            <h3 className="font-semibold text-foreground">Precisa de ajuda?</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                Escolha como quer falar com a gente
                            </p>
                        </div>

                        <div className="p-2 space-y-1">
                            {/* WhatsApp - Principal */}
                            <button
                                onClick={handleWhatsApp}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors text-left group"
                            >
                                <div className="p-2 bg-green-500/10 rounded-lg text-green-500 group-hover:bg-green-500/20 transition-colors">
                                    <WhatsAppIcon />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-foreground">WhatsApp</p>
                                    <p className="text-xs text-muted-foreground">Resposta rápida</p>
                                </div>
                                <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>

                            {/* Email */}
                            <button
                                onClick={handleEmail}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors text-left group"
                            >
                                <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500 group-hover:bg-blue-500/20 transition-colors">
                                    <Copy className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-foreground">Copiar Email</p>
                                    <p className="text-xs text-muted-foreground">{supportEmail}</p>
                                </div>
                                <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>

                            {/* Bug Report */}
                            <button
                                onClick={handleBugReport}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors text-left group"
                            >
                                <div className="p-2 bg-red-500/10 rounded-lg text-red-500 group-hover:bg-red-500/20 transition-colors">
                                    <Bug className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-foreground">Reportar Bug</p>
                                    <p className="text-xs text-muted-foreground">Encontrou um problema?</p>
                                </div>
                                <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>

                            {/* FAQ */}
                            <button
                                onClick={handleFAQ}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors text-left group"
                            >
                                <div className="p-2 bg-primary/10 rounded-lg text-primary group-hover:bg-primary/20 transition-colors">
                                    <HelpCircle className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-foreground">FAQ</p>
                                    <p className="text-xs text-muted-foreground">Perguntas frequentes</p>
                                </div>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle Button */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`
          w-14 h-14 rounded-xl shadow-lg flex items-center justify-center transition-colors
          ${isOpen
                        ? 'bg-secondary text-foreground'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }
        `}
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <X className="w-6 h-6" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="open"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <MessageCircle className="w-6 h-6" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>
        </div>
    );
}
